import { useState, useEffect, useCallback } from 'react';
import { QuestionDTO } from '../dtos';

interface ProgressiveLoadingState {
  visibleItems: QuestionDTO[];
  hasMore: boolean;
  isLoadingMore: boolean;
  loadedCount: number;
  totalCount: number;
}

interface UseProgressiveLoadingProps {
  items: QuestionDTO[];
  initialBatchSize?: number;
  incrementSize?: number;
  autoLoadThreshold?: number;
  enabled?: boolean;
}

export const useProgressiveLoading = ({
  items,
  initialBatchSize = 5,
  incrementSize = 3,
  autoLoadThreshold = 2,
  enabled = true,
}: UseProgressiveLoadingProps) => {
  const [state, setState] = useState<ProgressiveLoadingState>({
    visibleItems: [],
    hasMore: false,
    isLoadingMore: false,
    loadedCount: 0,
    totalCount: 0,
  });

  // Reset state when items change
  useEffect(() => {
    if (!enabled) {
      setState({
        visibleItems: items,
        hasMore: false,
        isLoadingMore: false,
        loadedCount: items.length,
        totalCount: items.length,
      });
      return;
    }

    const initialCount = Math.min(initialBatchSize, items.length);
    setState({
      visibleItems: items.slice(0, initialCount),
      hasMore: items.length > initialCount,
      isLoadingMore: false,
      loadedCount: initialCount,
      totalCount: items.length,
    });
  }, [items, initialBatchSize, enabled]);

  // Load more items
  const loadMore = useCallback(() => {
    if (!enabled || state.isLoadingMore || !state.hasMore) {
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoadingMore: true,
    }));

    // Simulate loading delay for better UX
    setTimeout(() => {
      setState((prev) => {
        const newLoadedCount = Math.min(prev.loadedCount + incrementSize, items.length);

        return {
          visibleItems: items.slice(0, newLoadedCount),
          hasMore: newLoadedCount < items.length,
          isLoadingMore: false,
          loadedCount: newLoadedCount,
          totalCount: items.length,
        };
      });
    }, 300); // Small delay to show loading state
  }, [items, incrementSize, state.isLoadingMore, state.hasMore, enabled]);

  // Auto-load when approaching end of visible items
  const handleScroll = useCallback(
    (scrollPosition: number, containerHeight: number, totalHeight: number) => {
      if (!enabled || state.isLoadingMore || !state.hasMore) {
        return;
      }

      // Calculate remaining items to show
      const remainingItems = state.totalCount - state.loadedCount;
      const shouldAutoLoad =
        remainingItems > 0 && state.totalCount - state.loadedCount <= autoLoadThreshold;

      // Or check scroll position
      const scrollThreshold = 0.8; // 80% scrolled
      const isNearBottom = (scrollPosition + containerHeight) / totalHeight >= scrollThreshold;

      if (shouldAutoLoad || isNearBottom) {
        loadMore();
      }
    },
    [state, loadMore, autoLoadThreshold, enabled]
  );

  // Get skeleton count for loading more state
  const getSkeletonCount = useCallback(() => {
    return Math.min(incrementSize, state.totalCount - state.loadedCount);
  }, [incrementSize, state.totalCount, state.loadedCount]);

  return {
    visibleItems: state.visibleItems,
    hasMore: state.hasMore,
    isLoadingMore: state.isLoadingMore,
    loadedCount: state.loadedCount,
    totalCount: state.totalCount,
    loadMore,
    handleScroll,
    getSkeletonCount,
    progressPercentage: state.totalCount > 0 ? (state.loadedCount / state.totalCount) * 100 : 100,
  };
};
